import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order, Route, Shipment, StopStatus } from '@prody/shared';
import { computeOrderTotals } from '@prody/shared';
import {
  Button, Code, CrateHeader, EmptyState, FactList, IconClose, IconPrint,
  ListPane, ManifestPane, Metric, PageHeader, Section, Segmented, Select,
  Sheet, Stamp, Toolbar, Workbench, toast,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useOrders, useRoutes, useShipments, useTeam, useUpdateShipment,
} from '~/platform/data/queries';
import { useHasManifestSpace } from '~/platform/hooks/useMediaQuery';
import { STOP_TONE } from '~/platform/domain/presentation';
import './deliveries.css';

/* ==================================================================
   LA LISTA DE ENTREGAS.

   One day on one sheet: every albarán leaving the dock, grouped by
   route in loading order, with the pile that has no route yet at the
   top — because that pile is the morning's first job. The sheet
   prints as-is; the bands survive as greys and the sequence numbers
   are the loading plan.
   ================================================================== */

type DayFilter = 'today' | 'tomorrow';

const addDays = (plain: string, days: number): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

/** The driver's chain, plus the honest exits. */
const STOP_NEXT: Record<StopStatus, readonly StopStatus[]> = {
  pending: ['en-route'],
  'en-route': ['arrived'],
  arrived: ['delivered', 'partial', 'failed'],
  delivered: [],
  partial: [],
  failed: [],
  skipped: [],
};

export default function DeliveriesPage() {
  const { t } = useTranslation(['deliveries', 'logistics', 'orders', 'common']);
  const { business, format, can } = useBusiness();
  const [dayFilter, setDayFilter] = useState<DayFilter>('today');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const hasManifestSpace = useHasManifestSpace();

  const today = format.tradingDay();
  const day = dayFilter === 'today' ? today : addDays(today, 1);

  const { data: shipments = [], isLoading } = useShipments(business.id, day);
  const { data: routes = [] } = useRoutes(business.id, day);
  const { data: orders = [] } = useOrders(business.id, { deliveryDate: day });
  const { data: team = [] } = useTeam(business.id);
  const updateShipment = useUpdateShipment(business.id, day);

  const canUpdate = can('shipment', 'update');

  const ordersById = useMemo(
    () => new Map<string, Order>(orders.map((o) => [o.id as string, o])),
    [orders],
  );
  const memberName = useMemo(() => {
    const byId = new Map(team.map((m) => [m.userId as string, m.name]));
    return (userId: string | null) => (userId ? byId.get(userId) ?? userId : null);
  }, [team]);

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const shipmentTotal = (shipment: Shipment) =>
    shipment.orderIds.reduce((sum, id) => {
      const order = ordersById.get(id as string);
      return order ? sum + computeOrderTotals(order, taxBpsFor).gross.minor : sum;
    }, 0);

  const lanes = useMemo(() => {
    const byRoute = new Map<string, Shipment[]>();
    for (const s of shipments) {
      const key = s.routeId ?? '';
      byRoute.set(key, [...(byRoute.get(key) ?? []), s]);
    }
    for (const list of byRoute.values()) list.sort((a, b) => a.sequence - b.sequence);
    const routeLanes = routes
      .map((route) => ({ route, stops: byRoute.get(route.id) ?? [] }))
      .sort((a, b) => a.route.name.localeCompare(b.route.name));
    return { unrouted: byRoute.get('') ?? [], routeLanes };
  }, [shipments, routes]);

  const delivered = shipments.filter((s) => s.status === 'delivered').length;
  const failed = shipments.filter((s) => s.status === 'failed' || s.status === 'partial').length;
  const crates = shipments.reduce((sum, s) => sum + s.crateCount, 0);

  const selected = shipments.find((s) => s.id === selectedId) ?? null;

  const manifest = selected ? (
    <StopManifest
      shipment={selected}
      route={routes.find((r) => r.id === selected.routeId) ?? null}
      routes={routes}
      ordersById={ordersById}
      driverName={memberName}
      canUpdate={canUpdate}
      onPatch={(patch, message) =>
        updateShipment.mutate(
          { shipmentId: selected.id, patch },
          {
            onSuccess: () => toast.ok(selected.number, { detail: message }),
            onError: () => toast.error(t('common:errors.saveFailed')),
          },
        )
      }
      busy={updateShipment.isPending}
      onClose={() => setSelectedId(null)}
    />
  ) : null;

  const stopRow = (stop: Shipment, seq: number | null) => {
    const total = shipmentTotal(stop);
    const lineCount = stop.orderIds.reduce(
      (sum, id) => sum + (ordersById.get(id as string)?.lines.length ?? 0),
      0,
    );
    return (
      <tr
        key={stop.id}
        className="pd-entrega"
        data-active={selectedId === stop.id || undefined}
        onClick={() => setSelectedId(stop.id)}
      >
        <td className="num pd-entrega__seq">{seq === null ? '—' : seq}</td>
        <td><Code>{stop.number}</Code></td>
        <td className="pd-entrega__customer">
          <span className="truncate">{stop.customerName}</span>
          <span className="t-small dim truncate">{stop.addressLabel}</span>
        </td>
        <td className="num pd-entrega__window">{format.timeWindow(stop.window)}</td>
        <td className="pd-entrega__orders">
          {stop.orderIds.map((id) => (
            <Code key={id}>{ordersById.get(id as string)?.number ?? id}</Code>
          ))}
          <span className="t-small faint">{t('lineCount', { count: lineCount })}</span>
        </td>
        <td className="num right">{stop.crateCount}</td>
        <td className="num right">
          {total > 0
            ? format.money({ minor: total, currency: business.settings.currency }, { bare: true })
            : '—'}
        </td>
        <td className="pd-entrega__status">
          <Stamp
            tone={STOP_TONE[stop.status]}
            weight={stop.status === 'delivered' ? 'solid' : 'outline'}
            size="sm"
          >
            {t(`logistics:stopStatus.${stop.status}`)}
          </Stamp>
        </td>
      </tr>
    );
  };

  const laneTable = (stops: Shipment[], sequenced: boolean) => (
    <table className="pd-entregas">
      <thead>
        <tr>
          <th className="stencil num-h">#</th>
          <th className="stencil">{t('albaran')}</th>
          <th className="stencil">{t('common:labels.customer')}</th>
          <th className="stencil">{t('common:labels.window')}</th>
          <th className="stencil">{t('orders:title')}</th>
          <th className="stencil right">{t('logistics:crates')}</th>
          <th className="stencil right">{t('common:labels.total')} ({format.currencySymbol()})</th>
          <th className="stencil">{t('common:labels.status')}</th>
        </tr>
      </thead>
      <tbody>{stops.map((s, i) => stopRow(s, sequenced ? i + 1 : null))}</tbody>
    </table>
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(day, 'weekday')}
        actions={
          <Button variant="quiet" icon={<IconPrint />} onClick={() => window.print()}>
            {t('common:actions.print')}
          </Button>
        }
      />

      <Toolbar
        trailing={
          <div className="pd-entregas__meter" data-print="hide">
            <Metric label={t('stops')} value={shipments.length} />
            <Metric label={t('logistics:stopStatus.delivered')} value={delivered} tone="ok" />
            {failed > 0 ? (
              <Metric label={t('incidents')} value={failed} tone="alert" />
            ) : null}
            <Metric label={t('noRoute')} value={lanes.unrouted.length} tone={lanes.unrouted.length ? 'warn' : undefined} />
            <Metric label={t('logistics:crates')} value={crates} />
          </div>
        }
      >
        <Segmented<DayFilter>
          label={t('orders:filters.day')}
          value={dayFilter}
          onChange={setDayFilter}
          options={[
            { value: 'today', label: t('common:labels.today') },
            { value: 'tomorrow', label: t('common:labels.tomorrow') },
          ]}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && shipments.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : (
          <div className="pd-entregas__sheet">
            {/* The pile with no route — the morning's first job, so it leads. */}
            {lanes.unrouted.length > 0 ? (
              <section className="pd-entregas__lane" data-unrouted>
                <header className="pd-entregas__lane-head">
                  <Stamp tone="warn">{t('noRoute')}</Stamp>
                  <span className="num faint">{lanes.unrouted.length}</span>
                </header>
                {laneTable(lanes.unrouted, false)}
              </section>
            ) : null}

            {lanes.routeLanes.map(({ route, stops }) => (
              <section key={route.id} className="pd-entregas__lane">
                <header className="pd-entregas__lane-head">
                  <h2 className="pd-entregas__route">{route.name}</h2>
                  {route.vehicleId ? <Code>{route.vehicleId}</Code> : null}
                  {memberName(route.driverId) ? (
                    <span className="t-small dim">
                      {t('logistics:driver')}: {memberName(route.driverId)}
                    </span>
                  ) : null}
                  <Stamp
                    tone={route.status === 'dispatched' ? 'warn' : route.status === 'completed' ? 'ok' : 'neutral'}
                    weight={route.status === 'completed' ? 'solid' : 'outline'}
                    size="sm"
                  >
                    {t(`routeStatus.${route.status}`)}
                  </Stamp>
                  <span className="num faint pd-entregas__lane-count">
                    {t('logistics:stops', { count: stops.length })}
                  </span>
                </header>
                {stops.length === 0 ? (
                  <p className="dim t-small pd-entregas__lane-empty">{t('laneEmpty')}</p>
                ) : (
                  laneTable(stops, true)
                )}
              </section>
            ))}
          </div>
        )}
      </ListPane>

      {hasManifestSpace ? (
        <ManifestPane open={Boolean(selected)}>{manifest}</ManifestPane>
      ) : (
        <Sheet
          open={Boolean(selected)}
          onOpenChange={(open) => !open && setSelectedId(null)}
          title={selected?.number ?? ''}
        >
          {manifest}
        </Sheet>
      )}
    </Workbench>
  );
}

/* ------------------------------------------------------------------ *
 * One stop, opened: the goods that go over the doorstep, the window,
 * the route it rides on, and the buttons a dispatcher (or a driver on
 * a phone) actually presses. Route assignment lives here too — the
 * unrouted pile is cleared one stop at a time.
 * ------------------------------------------------------------------ */

function StopManifest({
  shipment,
  route,
  routes,
  ordersById,
  driverName,
  canUpdate,
  onPatch,
  busy,
  onClose,
}: {
  shipment: Shipment;
  route: Route | null;
  routes: Route[];
  ordersById: Map<string, Order>;
  driverName: (id: string | null) => string | null;
  canUpdate: boolean;
  onPatch: (patch: Partial<Shipment>, message: string) => void;
  busy: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(['deliveries', 'logistics', 'orders', 'common']);
  const { format } = useBusiness();

  const nextStatuses = STOP_NEXT[shipment.status];
  const stopOrders = shipment.orderIds
    .map((id) => ordersById.get(id as string))
    .filter((o): o is Order => Boolean(o));

  return (
    <div className="pd-entrega-manifest">
      <CrateHeader
        handlingClass="neutral"
        code={shipment.number}
        title={shipment.customerName}
        subtitle={shipment.addressLabel}
        stamp={
          <Stamp
            tone={STOP_TONE[shipment.status]}
            weight={shipment.status === 'delivered' ? 'solid' : 'outline'}
          >
            {t(`logistics:stopStatus.${shipment.status}`)}
          </Stamp>
        }
        facts={[
          { label: t('common:labels.window'), value: <span className="num">{format.timeWindow(shipment.window)}</span> },
          { label: t('logistics:eta'), value: <span className="num">{format.dateTime(shipment.etaAt, 'time')}</span> },
          {
            label: t('logistics:crates'),
            value: (
              <span className="num">
                {shipment.crateCount}
                {shipment.cratesReturned > 0 ? ` / ${shipment.cratesReturned}` : ''}
              </span>
            ),
          },
          {
            label: t('logistics:route'),
            value: route ? route.name : t('noRoute'),
          },
        ]}
        actions={
          <Button
            variant="quiet"
            size="sm"
            iconOnly
            aria-label={t('common:actions.close')}
            icon={<IconClose />}
            onClick={onClose}
          />
        }
      />

      <div className="pd-entrega-manifest__body">
        {/* ---- Move it along ---- */}
        {canUpdate && nextStatuses.length > 0 ? (
          <Section title={t('common:labels.status')}>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              {nextStatuses.map((next, i) => (
                <Button
                  key={next}
                  variant={next === 'failed' ? 'danger' : i === 0 ? 'primary' : 'default'}
                  size="sm"
                  busy={busy}
                  onClick={() =>
                    onPatch(
                      {
                        status: next,
                        ...(next === 'arrived' ? { arrivedAt: new Date().toISOString() } : {}),
                        ...(next === 'delivered' || next === 'partial'
                          ? { completedAt: new Date().toISOString() }
                          : {}),
                      },
                      t(`logistics:stopStatus.${next}`),
                    )
                  }
                >
                  {t(`logistics:stopStatus.${next}`)}
                </Button>
              ))}
            </div>
          </Section>
        ) : null}

        {/* ---- Route assignment ---- */}
        {canUpdate ? (
          <Section title={t('logistics:route')}>
            <Select
              value={shipment.routeId ?? ''}
              aria-label={t('logistics:route')}
              onChange={(e) => {
                const routeId = e.target.value || null;
                const target = routes.find((r) => r.id === routeId);
                onPatch(
                  { routeId },
                  target ? t('assignedToRoute', { route: target.name }) : t('noRoute'),
                );
              }}
            >
              <option value="">{t('noRoute')}</option>
              {routes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Section>
        ) : null}

        {/* ---- What goes over the doorstep ---- */}
        <Section title={t('goods')}>
          {stopOrders.map((order) => (
            <div key={order.id} className="pd-entrega-manifest__order">
              <p className="row-baseline">
                <Code>{order.number}</Code>
                <span className="t-small dim">{t(`orders:status.${order.status}`)}</span>
              </p>
              <ul className="pd-entrega-manifest__lines">
                {order.lines.map((line) => (
                  <li key={line.id}>
                    <span className="truncate">{line.productName}</span>
                    <span className="num">{format.quantity(line.picked ?? line.ordered)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Section>

        {/* ---- Proof ---- */}
        {shipment.proof ? (
          <Section title={t('logistics:proof')}>
            <FactList
              columns={2}
              items={[
                { label: t('logistics:signedBy'), value: shipment.proof.signedBy ?? '—' },
                {
                  label: t('common:labels.time'),
                  value: <span className="num">{format.dateTime(shipment.proof.capturedAt, 'time')}</span>,
                },
              ]}
            />
          </Section>
        ) : null}

        {shipment.note ? (
          <Section title={t('common:labels.notes')}>
            <p className="t-small">{shipment.note}</p>
          </Section>
        ) : null}

        {driverName(route?.driverId ?? null) ? (
          <Section title={t('logistics:driver')}>
            <p className="t-small">{driverName(route?.driverId ?? null)}</p>
          </Section>
        ) : null}
      </div>
    </div>
  );
}
